export interface ConferenceSelectOptionModel {
  value: number;
  label: string;
}

export enum ConferenceApplicationStatus {
  Submitted = 1,
  UnderReview = 2,
  Approved = 3,
  Denied = 4,
  Cancelled = 5,
  Completed = 6,
  PendingHrReview = 7,
  PendingOversight = 8,
  PendingCvp = 9
}

export enum ConferenceUtilizationStatus {
  Utilized = 1,
  NotUtilized = 2
}

export interface ConferenceEligibilitySettingsModel {
  id?: number;
  iD?: number;
  ID?: number;
  accrualStartDate: string;
  kraThreshold: number;
  fileReviewThreshold: number;
  enableGoodStandingRule: boolean;
  goodStandingLookbackMonths: number;
  localNationalAdvanceDays: number;
  internationalAdvanceDays: number;
}

export interface ConferenceEligibilityLevelRuleModel {
  id?: number;
  iD?: number;
  ID?: number;
  roleCategoryID?: number;
  roleCategoryId?: number;
  RoleCategoryID?: number;
  levelID: number;
  levelLabel?: string;
  isEligible: boolean;
  requiredHours: number;
}

export interface ConferenceEligibilityRoleCategoryModel {
  id?: number;
  iD?: number;
  ID?: number;
  name: string;
  description?: string;
  appliesFileReviewRule: boolean;
  archived: boolean;
  jobTitleIDs: number[];
  levelRules: ConferenceEligibilityLevelRuleModel[];
}

export interface ConferenceEligibilityRoleCategoryUserModel {
  id?: number;
  iD?: number;
  ID?: number;
  roleCategoryID: number;
  roleCategoryName?: string;
  userID: number;
  employeeName?: string;
  createdDate?: string;
}

export interface ConferenceOpportunityModel {
  id?: number;
  iD?: number;
  ID?: number;
  title: string;
  location?: string;
  levelID: number;
  levelLabel?: string;
  startDate: string;
  endDate: string;
  applicationDeadline?: string;
  description?: string;
  statusID: number;
  statusLabel?: string;
  maxRecommendedParticipants?: number;
  archived: boolean;
  questions?: ConferenceOpportunityQuestionModel[];
}

export interface ConferenceOpportunityQuestionModel {
  id?: number;
  opportunityID?: number;
  prompt: string;
  helpText?: string;
  questionTypeID: number;
  questionTypeLabel?: string;
  isRequired: boolean;
  sortOrder: number;
  options: string[];
  archived?: boolean;
}

export interface ConferenceEligibilityConfigurationModel {
  accruedHoursRefreshWarning?: string;
  settings: ConferenceEligibilitySettingsModel;
  roleCategories: ConferenceEligibilityRoleCategoryModel[];
  userOverrides: ConferenceEligibilityRoleCategoryUserModel[];
  levels: ConferenceSelectOptionModel[];
  opportunityStatuses: ConferenceSelectOptionModel[];
  applicationStatuses: ConferenceSelectOptionModel[];
  questionTypes: ConferenceSelectOptionModel[];
}

export interface ConferenceEligibilityLevelResultModel {
  levelID: number;
  levelLabel: string;
  roleAllowsLevel: boolean;
  requiredHours: number;
  hasRequiredHours: boolean;
  meetsKra: boolean;
  meetsFileReview: boolean;
  meetsStanding: boolean;
  eligible: boolean;
  reasons: string[];
}

export interface ConferenceEligibilitySnapshotModel {
  userID: number;
  jobTitleID?: number;
  roleCategoryID?: number;
  roleCategoryName?: string;
  roleCategoryIsOverride: boolean;
  accrualStartDate: string;
  accruedHours: number;
  reservedHours: number;
  utilizedHours: number;
  availableHours: number;
  kraAverageScore?: number;
  kraThreshold: number;
  fileReviewScore?: number;
  fileReviewThreshold: number;
  hasActiveCaseload: boolean;
  isInGoodStanding: boolean;
  standingReason?: string;
  timing?: ConferenceTimingResult;
  levelResults: ConferenceEligibilityLevelResultModel[];
}

export interface ConferenceTimingResult {
  meetsTiming: boolean;
  requiredAdvanceDays: number;
  actualAdvanceDays: number;
  reason?: string;
}

export interface ConferenceApplicationAnswerModel {
  id?: number;
  questionID?: number;
  questionPrompt?: string;
  questionTypeID?: number;
  answerJson?: string;
  displayAnswer?: string;
  fileName?: string;
  fileType?: string;
  fileData?: string;
  hasFile?: boolean;
}

export interface ConferenceApplicationSubmitModel {
  opportunityID?: number;
  levelID: number;
  requestedTitle?: string;
  requestedLocation?: string;
  requestedStartDate?: string;
  requestedEndDate?: string;
  answers: ConferenceApplicationAnswerModel[];
}

export interface ConferenceApplicationStatusHistoryModel {
  id: number;
  statusID: number;
  statusLabel: string;
  notes?: string;
  isOverride: boolean;
  changedByUserID: number;
  changedByName: string;
  changedDate: string;
}

export interface ConferenceApplicationModel {
  id: number;
  userID: number;
  employeeName: string;
  employeeEmail?: string;
  opportunityID?: number;
  conferenceTitle: string;
  conferenceLocation?: string;
  conferenceStartDate?: string;
  conferenceEndDate?: string;
  levelID: number;
  levelLabel: string;
  statusID: number;
  statusLabel: string;
  plannedHours: number;
  reservedHours: number;
  submittedDate: string;
  decisionDate?: string;
  decisionNotes?: string;
  utilizationStatusID?: number;
  utilizationStatusLabel?: string;
  hoursPulled?: number;
  hoursPulledDate?: string;
  utilizedHours?: number;
  hoursRemaining?: number;
  utilizationDate?: string;
  utilizationByUserID?: number;
  utilizationByName?: string;
  utilizationNotes?: string;
  isException: boolean;
  exceptionReasons: string[];
  eligibilitySnapshot?: ConferenceEligibilitySnapshotModel;
  answers: ConferenceApplicationAnswerModel[];
  statusHistory: ConferenceApplicationStatusHistoryModel[];
}

export interface ConferenceApplicationReviewModel {
  statusID: number;
  notes: string;
  isOverride: boolean;
}

export interface ConferenceApplicationUtilizationModel {
  utilizationStatusID: number;
  hoursPulledDate: string;
  utilizedHours: number;
  utilizationDate: string;
  notes?: string;
}

export interface ConferenceHoursPulledModel {
  hoursPulledDate: string;
  hoursPulled: number;
}

export interface ConferenceEligibilityReportFilterModel {
  startDate?: string;
  endDate?: string;
  roleCategoryID?: number;
  departmentID?: number;
  conference?: string;
  levelID?: number;
  applicationStatusID?: number;
  utilizationStatusID?: number;
}

export interface ConferenceEligibilityReportRowModel {
  applicationID: number;
  userID: number;
  employeeName: string;
  jobTitle?: string;
  roleCategory?: string;
  departments?: string;
  conferenceTitle: string;
  conferenceStartDate?: string;
  conferenceEndDate?: string;
  levelID: number;
  levelLabel: string;
  applicationStatusID: number;
  applicationStatusLabel: string;
  utilizationStatusID?: number;
  utilizationStatusLabel: string;
  plannedHours: number;
  reservedHours: number;
  hoursPulled?: number;
  hoursPulledDate?: string;
  utilizedHours?: number;
  hoursRemaining?: number;
  utilizationDate?: string;
  notes?: string;
}

export interface ConferenceEligibilityReportModel {
  rows: ConferenceEligibilityReportRowModel[];
  roleCategories: ConferenceSelectOptionModel[];
  departments: ConferenceSelectOptionModel[];
  levels: ConferenceSelectOptionModel[];
  applicationStatuses: ConferenceSelectOptionModel[];
  utilizationStatuses: ConferenceSelectOptionModel[];
}

export interface ConferenceEligibilityEmployeeViewModel {
  userID: number;
  settings: ConferenceEligibilitySettingsModel;
  eligibility: ConferenceEligibilitySnapshotModel;
  openOpportunities: ConferenceOpportunityModel[];
  applications: ConferenceApplicationModel[];
  canReview: boolean;
  pendingReviewCount: number;
  helpText: string;
}

export interface ConferenceApplicationsResponseModel {
  applications: ConferenceApplicationModel[];
  statuses: ConferenceSelectOptionModel[];
  canReview: boolean;
  canReport: boolean;
}
